import { useSuspenseQuery } from "@apollo/client";
import { getRouteApi } from "@tanstack/react-router";
import { Calendar, Clock, Truck } from "lucide-react";
import { Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { destinationsTotalsQuery, sourcesTotalsQuery } from "../queries";
import { getCurrentMonth, getMonthDateRange } from "../utils/monthUtils";
import { ErrorCard } from "@/components/ErrorCard";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { getErrorMessage } from "@/lib/utils/errors";
import { formatInteger } from "@/lib/utils/formatting";

export interface SummaryTotals {
  numHead: number;
  numHeadPlanned: number;
  numHeadBooked: number;
  numHeadShipped: number;
}

const routeApi = getRouteApi("/(authed)/_layout/bookings/_tabbed/summary");

export function SummaryCards() {
  return (
    <div className="px-4 pb-4">
      <div className="grid grid-cols-1 gap-4 max-w-lg">
        <ErrorBoundary
          FallbackComponent={({ error }) => <ErrorCard title="Error" description={getErrorMessage(error)} />}
        >
          <Suspense fallback={<TotalHeadSkeleton />}>
            <SummaryCardsContent />
          </Suspense>
        </ErrorBoundary>
      </div>
    </div>
  );
}

function SummaryCardsContent() {
  const search = routeApi.useSearch();

  switch (search.view) {
    case "destinations":
      return <DestinationsTotalHeadCard />;
    case "sources":
      return <SourcesTotalHeadCard />;
    default:
      const _exhaustive: never = search.view;

      console.error("Unhandled view type:", _exhaustive);
      return <SourcesTotalHeadCard />;
  }
}

function DestinationsTotalHeadCard() {
  const totals = useDestinationsTotals();
  return <TotalHeadCard totals={totals} />;
}

function SourcesTotalHeadCard() {
  const totals = useSourcesTotals();
  return <TotalHeadCard totals={totals} />;
}

interface TotalHeadCardProps {
  totals: SummaryTotals;
}

function TotalHeadCard({ totals }: TotalHeadCardProps) {
  return (
    <Card className="p-6">
      <div className="space-y-4">
        <div className="text-sm font-medium text-foreground">Total Head</div>
        <div className="text-3xl font-bold">{formatInteger(totals.numHead)}</div>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-foreground/70" />
            <span className="text-foreground/70">Planned:</span>
            <span className="font-medium text-foreground/70">{formatInteger(totals.numHeadPlanned)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-foreground/70" />
            <span className="text-foreground/70">Booked:</span>
            <span className="font-medium text-foreground/70">{formatInteger(totals.numHeadBooked)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Truck className="w-4 h-4 text-foreground/70" />
            <span className="text-foreground/70">Shipped:</span>
            <span className="font-medium text-foreground/70">{formatInteger(totals.numHeadShipped)}</span>
          </div>
        </div>
      </div>
    </Card>
  );
}

function TotalHeadSkeleton() {
  return (
    <Card className="p-6">
      <div className="space-y-4">
        <Skeleton className="h-5 w-20" />
        <Skeleton className="h-9 w-16" />

        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <div className="flex items-center gap-2">
            <Skeleton className="w-4 h-4" />
            <Skeleton className="h-5 w-12" />
            <Skeleton className="h-5 w-8" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="w-4 h-4" />
            <Skeleton className="h-5 w-12" />
            <Skeleton className="h-5 w-8" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="w-4 h-4" />
            <Skeleton className="h-5 w-12" />
            <Skeleton className="h-5 w-8" />
          </div>
        </div>
      </div>
    </Card>
  );
}

function useQueryVariables() {
  const { activeBusinessUnitId } = useActiveBusinessUnit();
  const search = routeApi.useSearch();

  const currentMonthResult = getCurrentMonth();
  const currentMonth = currentMonthResult.success ? currentMonthResult.data : "january";
  const currentYear = new Date().getFullYear();

  const selectedMonth = search.month || currentMonth;
  const selectedYear = search.year || currentYear;

  const { fromDate, toDate } = getMonthDateRange(selectedMonth, selectedYear);

  return {
    businessUnit: activeBusinessUnitId || "",
    fromDate,
    toDate,
  };
}

const useDestinationsTotals = (): SummaryTotals => {
  const variables = useQueryVariables();
  const { data } = useSuspenseQuery(destinationsTotalsQuery, { variables, fetchPolicy: "cache-and-network" });

  const rawTotals = data.supplyChainBookingDestinationsSummary?.totals;

  return {
    numHead: rawTotals?.numHead || 0,
    numHeadPlanned: rawTotals?.numHeadPlanned || 0,
    numHeadBooked: rawTotals?.numHeadBooked || 0,
    numHeadShipped: rawTotals?.numHeadShipped || 0,
  };
};

const useSourcesTotals = (): SummaryTotals => {
  const variables = useQueryVariables();
  const { data } = useSuspenseQuery(sourcesTotalsQuery, { variables, fetchPolicy: "cache-and-network" });

  const rawTotals = data.supplyChainBookingSourcesSummary?.totals;

  return {
    numHead: rawTotals?.numHead || 0,
    numHeadPlanned: rawTotals?.numHeadPlanned || 0,
    numHeadBooked: rawTotals?.numHeadBooked || 0,
    numHeadShipped: rawTotals?.numHeadShipped || 0,
  };
};
